import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { getOpportunityLastSelectedStateDataByOpportunityId } from '../opportunityLastSelectedStates/mockData'
import {
  getOrderDataByOpportunityId,
  getOrderOverviewDataByOpportunityId,
} from '../orders/mockData'
import { getTaskListDataByOpportunityId } from '../tasks/mockData'
import { getCallListDataByOpportunityId } from '../calls/mockData'
import { getEmailListDataByOpportunityId } from '../emails/mockData'
import { requireAuthenticatedUser } from '../../decorators'
import {
  getSupplierListDataByOpportunityId,
  getOpportunityListData,
  getOpportunityOverviewData,
  getOpportunityMSServiceListDataByOpportunityId,
} from './mockData'
import type {
  IOpportunity,
  IOpportunityLastSelectedState,
  IOpportunityOverview,
} from '~/types/opportunity/models'
import type { IEntitySelectItemWithOptionalAvatar } from '~/types/models'

interface GetOpportunitiesResponse {
  data: IOpportunity[]
}

async function getOpportunities(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const responseBody: GetOpportunitiesResponse = {
    data: await getOpportunityListData(),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getOpportunity(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  const list = await getOpportunityListData()
  if (!list) {
    return res(ctx.status(404), ctx.json({ message: 'Resource not found.' }))
  }
  const opportunity = list.find((obj) => obj.id == id)
  if (opportunity) {
    const responseBody = { data: opportunity }
    return res(ctx.status(200), ctx.json(responseBody))
  } else {
    return res(ctx.status(404), ctx.json({ message: 'Resource not found.' }))
  }
}

interface GetOpportunityOverviewResponse {
  data: IOpportunityOverview
}

async function getOpportunityOverviewById(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetOpportunityOverviewResponse = {
      data: await getOpportunityOverviewData(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't display Opportunity ${id} Overview: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

interface GetOpportunityLastSelectedStateResponse {
  data: IOpportunityLastSelectedState
}

async function getOpportunityLastSelectedStateById(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetOpportunityLastSelectedStateResponse = {
      data: await getOpportunityLastSelectedStateDataByOpportunityId(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Opportunity ${id} last selected data: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

async function getOrderByOpportunityId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning order for opportunity "${id}".`)
  const data = await getOrderDataByOpportunityId(id) // may be null. That's a valid response.
  return res(ctx.status(200), ctx.json({ data }))
}

async function getOrderOverviewByOpportunityId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning order for opportunity "${id}".`)
  const data = await getOrderOverviewDataByOpportunityId(id) // may be null. That's a valid response.
  return res(ctx.status(200), ctx.json({ data }))
}

async function geOpportunityMSServicesByyOpportunityId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning opportunityMSServices for opportunity "${id}".`)
  const data = await getOpportunityMSServiceListDataByOpportunityId(id) // may be empty array. That's a valid response.
  return res(ctx.status(200), ctx.json({ data }))
}

async function getTasksByOpportunityId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning tasks for opportunity "${id}".`)
  const data = await getTaskListDataByOpportunityId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

async function getCallsByOpportunityId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning calls for opportunity "${id}".`)
  const data = await getCallListDataByOpportunityId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

async function getEmailsByOpportunityId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning emails for opportunity "${id}".`)
  const data = await getEmailListDataByOpportunityId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

interface GetSuppliersFromOpportunityResponse {
  data: IEntitySelectItemWithOptionalAvatar[]
}

async function getSuppliersFromOpportunity(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const onlyWithReadyForContractMSServices =
    req.url.searchParams.get('onlywithreadyforcontractmsservices') === 'true'
  try {
    const responseBody: GetSuppliersFromOpportunityResponse = {
      data: await getSupplierListDataByOpportunityId(
        id,
        onlyWithReadyForContractMSServices
      ),
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Suppliers from opportunityMasterService ${id}: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

// post

const postOpportunity = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(req, res, ctx, getOpportunityListData)
}

const patchOpportunity = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getOpportunityListData)
}

const deleteOpportunity = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getOpportunityListData)
}

export const handlers = [
  {
    method: 'get',
    path: '/api/opportunities',
    handler: getOpportunities,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id',
    handler: getOpportunity,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/overview',
    handler: getOpportunityOverviewById,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/lastselectedstate',
    handler: getOpportunityLastSelectedStateById,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/order',
    handler: getOrderByOpportunityId,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/orderoverview',
    handler: getOrderOverviewByOpportunityId,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/opportunitymsservices',
    handler: geOpportunityMSServicesByyOpportunityId,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/tasks',
    handler: getTasksByOpportunityId,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/calls',
    handler: getCallsByOpportunityId,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/emails',
    handler: getEmailsByOpportunityId,
  },
  {
    method: 'get',
    path: '/api/opportunities/:id/suppliers',
    handler: getSuppliersFromOpportunity,
  },
  {
    method: 'post',
    path: '/api/opportunities',
    handler: postOpportunity,
  },
  {
    method: 'patch',
    path: '/api/opportunities/:id',
    handler: patchOpportunity,
  },
  {
    method: 'delete',
    path: '/api/opportunities/:id',
    handler: deleteOpportunity,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
